// AI engine: stateless function that picks a move and analyzes a position.
// Used both directly (main thread) and inside the Worker.

import { Play, applyPlay } from "../engine/moves";
import { Position, Side, hashPosition, mirror } from "../engine/position";
import { checkWin } from "../engine/rules";
import { LEVELS, TUTOR_CONFIG, Difficulty } from "./levels";
import { rankPlays, rankPlaysDeep, pickWithNoise } from "./search";
import { bookEquities } from "./book";
import { heuristicEvaluator } from "./heuristic";
import { Evaluator } from "./evaluator";
import { AnyNeuralEvaluator, OutcomeProbs } from "./neural";
import { Met } from "./met";
import {
  CubeAction,
  TakeAction,
  decideCubeAction,
  decideTakeDrop,
} from "./cubeDecision";

let neuralEv: AnyNeuralEvaluator | null = null;
export function setNeuralEvaluator(ev: AnyNeuralEvaluator | null) {
  neuralEv = ev;
}
export function hasNeural(): boolean {
  return neuralEv !== null;
}

// Singleton MET for cube decisions. Loaded once at startup (see worker.ts
// and main.ts) and shared. Cube decisions return "no_double" / "take" if
// the MET hasn't been loaded yet.
let cubeMet: Met | null = null;
export function setMet(m: Met | null) {
  cubeMet = m;
}
export function hasMet(): boolean {
  return cubeMet !== null;
}

export function decideCube(p: Position, side: Side): CubeAction {
  if (!cubeMet) return "no_double";
  // Use the analysis-tier evaluator's outcome probabilities so cube
  // decisions align with what the tutor would see for this position.
  const ev = evaluatorFor(LEVELS.expert.analysisEvaluator);
  return decideCubeAction(p, side, evaluatorOutcomes(ev, p), cubeMet);
}

export function decideTake(p: Position, side: Side): TakeAction {
  if (!cubeMet) return "take";
  const ev = evaluatorFor(LEVELS.expert.analysisEvaluator);
  return decideTakeDrop(p, side, evaluatorOutcomes(ev, p), cubeMet);
}

function evaluatorFor(name: string): Evaluator {
  switch (name) {
    case "neural":
      return neuralEv ?? heuristicEvaluator;
    case "pubeval":
    case "heuristic":
    default:
      return heuristicEvaluator;
  }
}

export function pickMove(p: Position, legalPlays: Play[], difficulty: Difficulty): Play {
  if (legalPlays.length === 0) return [];
  if (legalPlays.length === 1) return legalPlays[0];
  const cfg = LEVELS[difficulty];
  // Opening / reply book: when this (position, dice) is booked, rank by the
  // book's points equity. The level's noise still applies, so weaker tiers
  // keep some opening variety instead of always playing the single best.
  const be = bookEquities(p, legalPlays, p.dice);
  if (be && be.complete) {
    const scored = legalPlays.map((play, i) => ({ play, equity: be.pointsEq[i] }));
    scored.sort((a, b) => b.equity - a.equity);
    return pickWithNoise(scored, cfg.noise);
  }
  const ev = evaluatorFor(cfg.evaluator);
  const ranked = rankPlays(p, legalPlays, ev, cfg.plies);
  return pickWithNoise(ranked, cfg.noise);
}

export interface AnalysisResult {
  bestPlay: Play;
  // Gammon-aware points equity of bestPlay. Invariant: equals the max over
  // all finite entries of `pointsEquities` — bestPlay, the ranking, the diff
  // column, and the blunder classification all share this one metric.
  bestEquity: number;
  // Gammon-aware points equity per legalPlays entry (input order). The
  // ranking metric: what "best" and per-move loss are measured in.
  pointsEquities: number[];
  // pWin - pLoss per legalPlays entry (input order). The equity bar's
  // metric; Win% = (gameEquity + 1) / 2 exactly, since pWin + pLoss = 1.
  gameEquities: number[];
}

// Extract outcome probabilities from any Evaluator. For evaluators with a
// native evaluateOutcomes (the 4-output neural net), use it directly. For
// scalar-only evaluators (heuristic, 1-output neural), reconstruct as a no-
// gammon outcome distribution from the scalar equity. The scalar is assumed
// to be in [-1, +1] and represents 2 * p_win - 1.
function evaluatorOutcomes(ev: Evaluator, p: Position): OutcomeProbs {
  const withOutcomes = ev as Evaluator & { evaluateOutcomes?: (p: Position) => OutcomeProbs };
  if (typeof withOutcomes.evaluateOutcomes === "function") {
    return withOutcomes.evaluateOutcomes(p);
  }
  const eq = Math.max(-1, Math.min(1, ev.evaluate(p)));
  const pWin = (eq + 1) / 2;
  return { pWin, pGammonWin: 0, pLoss: 1 - pWin, pGammonLoss: 0 };
}

// Signed game-win probability from the on-roll player's POV after applying
// `play`, range [-1, +1]. This is pWin - pLoss — a faithful "how likely am I
// to win this game" measure, independent of match score AND independent of
// gammon premium. A 75% gammon-win position reads +0.5 (75% chance of any
// win), NOT +1.0 (clamped expected points), so the bar tracks win likelihood
// rather than expected points scored.
function gameEquityForPostMove(
  pre: Position,
  play: Play,
  ev: Evaluator,
): number {
  const after = applyPlay(pre, play);
  const term = checkWin(after);
  if (term !== null) {
    // Terminal: we either won (+1) or lost (-1). basePoints (single/gammon/
    // backgammon) affects the match score, not the bar — the bar is in
    // probability-of-winning units.
    return term.winner === pre.turn ? 1 : -1;
  }
  // Non-terminal: evaluator from opponent's POV (mirror flips us<->them);
  // pWin from us POV is the mirror's pLoss.
  const oppOutcomes = evaluatorOutcomes(ev, mirror(after));
  const pWin = oppOutcomes.pLoss;
  const pLoss = oppOutcomes.pWin;
  return pWin - pLoss;
}

export function analyzeMove(
  p: Position,
  legalPlays: Play[],
  difficulty: Difficulty,
  awayUs?: number,
  awayThem?: number,
): AnalysisResult {
  if (legalPlays.length === 0) {
    return { bestPlay: [], bestEquity: 0, pointsEquities: [], gameEquities: [] };
  }

  // 1) Opening / reply book. Independent of opponent difficulty: grade against
  // gnubg-derived equities so a near-tie (within the threshold) is never
  // flagged. Ranking, bestEquity, and per-play loss are all in points equity
  // (gammon-aware), matching the search path below; game equities ride along
  // for the equity bar and Win% display.
  const be = bookEquities(p, legalPlays, p.dice);
  if (be && be.complete) {
    let bestI = 0;
    for (let i = 1; i < legalPlays.length; i++) {
      if (be.pointsEq[i] > be.pointsEq[bestI]) bestI = i;
    }
    return {
      bestPlay: legalPlays[bestI],
      bestEquity: be.pointsEq[bestI],
      pointsEquities: be.pointsEq,
      gameEquities: be.gameEq,
    };
  }

  // 2) Strong, difficulty-independent analysis: the tutor and equity bar always
  // use the neural evaluator at a deep, filtered search (TUTOR_CONFIG) — NOT
  // the opponent's level. Playing Casual must not give you 0-ply coaching.
  void difficulty;
  const ev = evaluatorFor(TUTOR_CONFIG.evaluator);
  // Score every play (no noise) with the filtered deep search. It dedupes by
  // final internally, so `scored` has one entry per unique final. We map
  // equities back to every input play via the final-position hash, so two
  // orderings reaching the same final share an equity (which is correct —
  // they ARE equivalent outcomes). The scalar score determines the play
  // ranking; a shared per-call cache memoizes overlapping leaves.
  // Only the 3-ply path is filtered (it would otherwise be too slow). At 1/2
  // ply we search exhaustively so the tutor's "best" is exact — never pruned.
  const filtered = TUTOR_CONFIG.plies >= 3;
  const scored = rankPlaysDeep(p, legalPlays, ev, {
    plies: TUTOR_CONFIG.plies,
    keepTop: filtered ? TUTOR_CONFIG.keepTop : Infinity,
    keepWindow: filtered ? TUTOR_CONFIG.keepWindow : Infinity,
    innerKeep: filtered ? TUTOR_CONFIG.innerKeep : Infinity,
    cache: new Map<string, number>(),
  });
  const eqByFinal = new Map<string, number>();
  for (const s of scored) {
    const h = hashPosition(applyPlay(p, s.play));
    eqByFinal.set(h, s.equity);
  }
  // These search scores ARE points equities: the neural evaluate returns
  // pWin + pGammonWin - pLoss - pGammonLoss and terminals score ±basePoints.
  const pointsEquities = legalPlays.map((pl) => {
    const h = hashPosition(applyPlay(p, pl));
    return eqByFinal.get(h) ?? -Infinity;
  });
  let bestI = 0;
  for (let i = 1; i < pointsEquities.length; i++) {
    if (pointsEquities[i] > pointsEquities[bestI]) bestI = i;
  }
  // Also report cubeless game equity per play (in [-1, +1]) for the equity
  // bar and Win% display. The MET-aware (match-equity) version was confusing
  // in long matches: a 95%-won race showed only +0.13 because the single
  // game's contribution to the match was small. Game equity matches user
  // intuition — "how dominant is my position right now" — independent of
  // match score.
  void awayUs; void awayThem;  // reserved for future match-aware tweaks
  const gameByFinal = new Map<string, number>();
  for (const s of scored) {
    const h = hashPosition(applyPlay(p, s.play));
    if (!gameByFinal.has(h)) {
      gameByFinal.set(h, gameEquityForPostMove(p, s.play, ev));
    }
  }
  const gameEquities = legalPlays.map((pl) => {
    const h = hashPosition(applyPlay(p, pl));
    return gameByFinal.get(h) ?? -Infinity;
  });
  return {
    bestPlay: legalPlays[bestI],
    bestEquity: pointsEquities[bestI],
    pointsEquities,
    gameEquities,
  };
}
